'use client';

import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useAllCategories } from "@/src/entities/category/model/hooks/useAllCategories";
import { CategoryCardFull } from "@/src/entities/category/ui/CategoryCardFull";
import { Footer } from "@/src/widgets";
import { Header } from "@/src/widgets/header/Header";
import { Typography } from "@/src/shared/ui/typography/Typography";
import { getLocalizedName, type Locale } from '@hummii/shared-types';

export default function CategoryPage() {
  const t = useTranslations('categories');
  const locale = useLocale() as Locale;

  // Fetch categories from server
  const { categoryTree, loading, error } = useAllCategories();

  // Filter only root categories (parentId === null)
  const rootCategories = categoryTree.filter((category) => category.parentId === null);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      {/* Main Content Container */}
      <main className="container-auto flex-1 mx-auto">
        {/* Page Title */}
        <div className="mb-8 md:mb-10 lg:mb-12">
          <Typography variant="h1" weight="bold" className="text-text-primary">
            {t('pageTitle')}
          </Typography>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Typography variant="body" color="secondary">
              {t('loading')}
            </Typography>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex justify-center items-center py-12">
            <Typography variant="body" color="error">
              {t('error')}: {error}
            </Typography>
          </div>
        )}

        {/* Categories Grid - Responsive: 1 col (mobile), 2 cols (tablet), 4 cols (desktop) */}
        {!loading && !error && (
          <div className="grid grid-cols-[minmax(0,408px)] md:grid-cols-[repeat(2,minmax(0,374px))] lg:grid-cols-[repeat(4,minmax(0,305px))] gap-x-[20px] gap-y-[40px] justify-center">
            {rootCategories.map((category, index) => {
              // Get subcategories from children
              const subcategories = category.children || [];

              return (
                <CategoryCardFull
                  key={category.id}
                  index={index}
                  icon={category.icon}
                  imageUrl={category.imageUrl}
                  categoryId={category.id}
                  categoryName={getLocalizedName(category, locale)}
                  categorySlug={category.slug}
                  subcategories={subcategories.map((sub) => ({
                    name: getLocalizedName(sub, locale),
                    slug: sub.slug,
                  }))}
                />
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};